import { useContext, useMemo, useState } from "react";
import { Alert, AlertTitle, Box, Icon, LinearProgress, Paper, Stack, Step, StepLabel, Stepper, Typography, useTheme } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { addMinutes, format } from "date-fns";
import { httpsCallable } from "firebase/functions";
import DataCell from "@/components/DataCell";
import Price from "@/components/Price";
import { Order, OrderItem, OrderStatus, PaymentMethodsType } from "@/types";
import { useAppSelector, useUserProfile } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import { functions } from "@/firebase";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { getOrderTotal } from "@/utils/order";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon, ArrowUp01Icon, CheckmarkCircle02Icon, CreditCardPosIcon } from "@hugeicons/core-free-icons";

export const THANKS_DELIVERED_IMAGE_URL =
  "https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fdelivered-order.png?alt=media&token=9c58cf7a-310a-49c3-9c17-5a83df437c00";

interface Stage {
  id: OrderStatus;
  title: string;
  flows: string[];
  image: React.ReactNode;
}

const stages: Stage[] = [
  {
    id: OrderStatus.REVIEWING,
    title: "Revisando",
    flows: ["delivery", "pick-up", "dine-in"],
    image: <img width="175px" alt="En revisión" src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fwaiter-reviewing.webp?alt=media&token=29eb5142-3b1a-4e40-900f-3c06d808b86d" />
  },
  {
    id: OrderStatus.PENDING_PAYMENT,
    title: "Pago",
    flows: ["delivery", "pick-up", "dine-in"],
    image: <img width="175px" alt="Esperando pago" src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fwaiter-reviewing.webp?alt=media&token=29eb5142-3b1a-4e40-900f-3c06d808b86d" />
  },
  {
    id: OrderStatus.PREPARING,
    title: "Preparando",
    flows: ["delivery", "pick-up", "dine-in"],
    image: <img width="175px" alt="Preparando" src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fpreparing-food.webp?alt=media&token=0778e949-5031-4e6b-82f6-92730fb02bd3" />
  },
  {
    id: OrderStatus.SENDING,
    title: "Enviando",
    flows: ["delivery"],
    image: <img width="175px" alt="Enviando" src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fdriver-sending.webp?alt=media&token=dcadb0fa-2bed-4798-a984-8707970ae79c" />
  },
  {
    id: OrderStatus.WAITING,
    title: "Pedido Listo",
    flows: ["pick-up"],
    image: <img width="175px" alt="Pedido Listo" src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fpreparing.webp?alt=media&token=e316bfe9-b0fa-41ba-9216-23c69e35b0a0" />
  },
];

interface Props {
  order: Order;
  isCompleted?: boolean;
}

const StatusView = ({ order, isCompleted = false }: Props) => {
  const { palette } = useTheme();
  const [openSnackbar] = useSnackbar();
  const { organization } = useContext(WebsiteContext);
  const activeOrgId = useAppSelector((state) => state.ui.activeOrganization);
  const organizationId = order?.organizationId || organization?.id || activeOrgId;
  const { profile } = useUserProfile(order?.client as unknown as string);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);

  const stripeEnabled = Boolean(organization?.settings?.paymentMethods?.stripeEnabled);
  const isCardPayment = order?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD;
  const isOrderApproved = [
    OrderStatus.PENDING_PAYMENT,
    OrderStatus.PREPARING,
    OrderStatus.SENDING,
    OrderStatus.WAITING,
  ].includes(order?.status as OrderStatus);
  const isOrderPaid = order?.paymentDetails?.status === "paid" || Boolean(order?.paidAt);
  const hasStripePayment =
    (stripeEnabled && isCardPayment) ||
    isOrderPaid ||
    Boolean(order?.paymentDetails?.stripeSessionId) ||
    order?.paymentDetails?.status === "paid" ||
    order?.paymentDetails?.status === "pending" ||
    order?.status === OrderStatus.PENDING_PAYMENT;

  const effectiveStatus = useMemo(() => {
    if (hasStripePayment && !isOrderPaid && order?.status === OrderStatus.PREPARING) {
      return OrderStatus.PENDING_PAYMENT;
    }
    return order?.status;
  }, [hasStripePayment, isOrderPaid, order?.status]);

  const deliveryFee =
    typeof order?.deliveryFee === "number"
      ? order.deliveryFee
      : typeof order?.serviceFee === "number"
        ? order.serviceFee
        : 0;
  const totalAmount = getOrderTotal((order?.items || []) as OrderItem[], organization?.settings?.includeTax, Number(deliveryFee));

  const handleProceedToPayment = async () => {
    if (!organizationId || !order?.id) return;
    try {
      setIsRedirectingToStripe(true);
      const createSession = httpsCallable<
        { organizationId: string; orderId: string; returnUrl: string },
        { sessionId: string; url: string }
      >(functions, "createCheckoutSession");

      const returnUrl = window.location.origin + window.location.pathname;
      const response = await createSession({
        organizationId,
        orderId: order.id,
        returnUrl,
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        openSnackbar("No se pudo obtener el enlace de pago", "error");
        setIsRedirectingToStripe(false);
      }
    } catch (error: any) {
      console.error("Error al iniciar sesión de Stripe:", error);
      openSnackbar(error?.message || "Ocurrió un error al preparar el pago con tarjeta", "error");
      setIsRedirectingToStripe(false);
    }
  };

  const isDeliveredOrCompleted =
    isCompleted || order?.status === OrderStatus.COMPLETED || order?.status === OrderStatus.DELIVERED;


  const stagesByFlow = useMemo(() => {
    if (!order?.flow) return [];
    return stages
      .filter((stage) => {
        if (!stage.flows.includes(order?.flow as string)) return false;
        if (stage.id === OrderStatus.PENDING_PAYMENT) {
          return hasStripePayment;
        }
        return true;
      })
      .map((stage) =>
        stage.id === OrderStatus.PENDING_PAYMENT && isOrderPaid
          ? { ...stage, title: "Pagado" }
          : stage
      );
  }, [order?.flow, hasStripePayment, isOrderPaid]);

  const activeStep = useMemo(() => {
    return stagesByFlow.findIndex((s) => s.id === effectiveStatus) ?? 0;
  }, [effectiveStatus, stagesByFlow]);

  const completedStages = useMemo(() => {
    if (order?.flow === "pick-up") {
      return [
        { id: "reviewed", title: "Revisado" },
        ...(hasStripePayment ? [{ id: "paid", title: "Pagado" }] : []),
        { id: "prepared", title: "Preparado" },
        { id: "ready", title: "Listo" },
      ];
    }
    return [
      { id: "reviewed", title: "Revisado" },
      ...(hasStripePayment ? [{ id: "paid", title: "Pagado" }] : []),
      { id: "prepared", title: "Preparado" },
      { id: "delivered", title: "Entregado" },
    ];
  }, [order?.flow, hasStripePayment]);

  const customerData = useMemo(() => {
    if (profile) {
      return profile;
    }

    if (order?.client) {
      return order?.client;
    }

    return null;
  }, [order?.client, profile]);

  return (
    <>
      <Stack direction={"row"} spacing={2}>
        <Paper sx={{ width: "100%", p: 2, alignContent: "center", borderRadius: "10px" }}>
          <Stack direction={"row"} spacing={2} alignItems={"center"} justifyContent={"space-between"}>
            <Icon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <HugeiconsIcon icon={ArrowUp01Icon} color="#015FEE" size={20} />
            </Icon>
            <Typography variant="h4" fontWeight={500}>
              {order?.createdAt ? format(new Date(order?.createdAt), "p") : "N/A"}
            </Typography>
          </Stack>
        </Paper>
        <Paper sx={{ width: "100%", p: 2, alignContent: "center", borderRadius: "10px" }}>
          <Stack direction={"row"} spacing={2} alignItems={"center"} justifyContent={"space-between"}>
            <Icon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isDeliveredOrCompleted ? (
                <HugeiconsIcon icon={CheckmarkCircle02Icon} color="#015FEE" size={20} strokeWidth={2.5} />
              ) : (
                <HugeiconsIcon icon={ArrowDown01Icon} color="#BDBDBD" size={20} />
              )}
            </Icon>
            <Typography variant="h4" fontWeight={500}>
              {isDeliveredOrCompleted
                ? order?.completedAt
                  ? format(new Date(order.completedAt), "p")
                  : order?.createdAt
                    ? format(new Date(order.createdAt), "p")
                    : "N/A"
                : order?.createdAt
                  ? format(addMinutes(order?.createdAt, 60), "p")
                  : "N/A"}
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <Stack>
        <Paper sx={{ borderRadius: "14px", overflow: "hidden" }}>
          {isDeliveredOrCompleted ? (
            <>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  pt: 3,
                  pb: 2,
                  px: 2,
                  background: "linear-gradient(180deg, #FFFFFF 60%, rgba(221, 234, 253, 0.45) 100%)",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "180px",
                    mb: 1,
                  }}
                >
                  <img
                    src={THANKS_DELIVERED_IMAGE_URL}
                    alt="Pedido Entregado"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "190px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  textAlign="center"
                  sx={{ color: "#07154E", fontSize: "1.45rem", mb: 0.5 }}
                >
                  {order?.flow === "pick-up" ? "Pedido Listo" : "Pedido Entregado"}
                </Typography>
                <Typography
                  variant="body1"
                  textAlign="center"
                  sx={{ color: "#07154E", fontWeight: 500, fontSize: "1.05rem" }}
                >
                  ¡Gracias por tu preferencia!
                </Typography>
              </Stack>

              <Stack spacing={2} px={1} py={3}>
                <Stepper
                  activeStep={completedStages.length}
                  alternativeLabel
                  sx={{
                    width: "100%",
                    "& .MuiStepConnector-line": {
                      borderColor: "#E0E0E0",
                      borderTopWidth: 1.5,
                    },
                  }}
                >
                  {completedStages.map((stage) => (
                    <Step key={stage.id} completed={true}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              backgroundColor: palette.success.main,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                            }}
                          >
                            <HugeiconsIcon icon={CheckmarkCircle02Icon} size={15} strokeWidth={2.8} />
                          </Box>
                        )}
                        sx={{
                          "& .MuiStepLabel-label": {
                            mt: 1,
                            color: "#1e293b",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                          },
                        }}
                      >
                        {stage.title}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Stack>
            </>
          ) : (
            <>
              <Stack
                height={"200px"}
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  position: "relative",
                }}
              >
                {stagesByFlow[activeStep]?.image ? stagesByFlow[activeStep].image : null}
              </Stack>

              <LinearProgress variant="indeterminate" color="primary" sx={{ height: 3 }} />

              <Stack spacing={2} px={1} py={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {stagesByFlow.map((stage) => {
                    const stageProps: { completed?: boolean } = {};
                    const labelProps: {
                      optional?: React.ReactNode;
                    } = {};

                    return (
                      <Step
                        key={stage.id}
                        {...stageProps}
                        sx={{
                          "& .MuiStepLabel-iconContainer .Mui-completed": {
                            color: palette.success.main,
                          },
                        }}
                      >
                        <StepLabel {...labelProps}>{stage.title}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </Stack>
            </>
          )}
        </Paper>
        
      </Stack>

      {!isDeliveredOrCompleted && customerData?.location?.address ? (
        <Stack spacing={2}>
          <Paper sx={{ width: "100%", p: 2, alignContent: "center", borderRadius: "10px" }}>
            <DataCell vertical label="Instrucciones de llegada" value={customerData.location.address ?? ""} />
          </Paper>
        </Stack>
      ) : null}

      {hasStripePayment && isOrderApproved && !isOrderPaid ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2,
              borderRadius: "14px",
              border: "1px solid",
              borderColor: "warning.main",
              backgroundColor: "#FFFDF5",
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <HugeiconsIcon icon={CreditCardPosIcon} color={palette.warning.dark} size={26} />
                <Typography variant="h4" fontWeight={700} color="text.primary">
                  Pago requerido para preparar tu pedido
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                El comercio ha confirmado tu pedido. Por favor completa el pago con tu tarjeta de crédito o débito para continuar con la preparación.
              </Typography>
              <LoadingButton
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                loading={isRedirectingToStripe}
                onClick={handleProceedToPayment}
                sx={{ borderRadius: "10px", fontWeight: 700, py: 1.2 }}
              >
                Pagar ahora (<Price currency="NIO">{totalAmount}</Price>)
              </LoadingButton>
            </Stack>
          </Paper>
        ) : null}

      {hasStripePayment && isOrderPaid ? (
          <Alert
            icon={<HugeiconsIcon icon={CheckmarkCircle02Icon} size={22} />}
            severity="success"
            sx={{ mb: 2, borderRadius: "12px" }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>Pago completado</AlertTitle>
            Tu pago con tarjeta ha sido procesado exitosamente.
          </Alert>
        ) : null}

    </>
  );
};

export default StatusView;
